import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import useDeviceDetect from 'Hooks/useDeviceDetect';
import { appConfigStore } from 'Models/AppConfigStore';
import { UserEntity } from 'Models/Entities';
import { store } from 'Models/Store';
import { ROUTES } from 'Constants';
import UserEntitlementsWrapper from 'Modules/AnytimeTicket/Components/UserEntitlements/UserEntitlementsWrapper';
import StaffCommunicationPreferences from 'Modules/CommunicationPreferences/Components/StaffCommunicationPreferences';
import { useAnytimeProductList } from 'Modules/AnytimeTicket/Queries/useAnytimeProductList';
import { changeEmailRequestByStaff, cancelChangeEmailRequest } from 'Services/Api/AccountService';
import { resendVerification } from 'Services/Api/_HumanWritten/RegisterService';
import alertToast from 'Util/ToastifyUtils';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { formatStandardDate, formatStandardDateTime } from 'Util/_HumanWritten/DateUtils';
import { getCustomerDisplayName } from 'Util/_HumanWritten/CustomerDisplay';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import If from 'Views/Components/If/If';
import AccountDetailsVouchers from 'Views/Components/_HumanWritten/AccountDetails/VoucherSection/GiftVouchersWrapper';
import AccountDetailsVehicles from 'Views/Components/_HumanWritten/AccountDetails/VehiclesSection/AccountDetailsVehicles';
import DetailFields from 'Views/Components/_HumanWritten/AccountDetails/AccountFields/DetailFields';
import UserPricingFields from 'Views/Components/_HumanWritten/AccountDetails/AccountFields/UserPricingFields';
import { saveUser } from 'Views/Components/_HumanWritten/AccountDetails/AccountFields/AccountFieldsUtils';
import { UserNotesModal } from 'Views/Components/_HumanWritten/AccountDetails/UserNotes/UserNotesModal';
import { UserInvoiceListComponent } from 'Views/Components/_HumanWritten/UserInvoiceList/UserInvoiceListComponent';
import { confirmAndConvertGuest } from 'Views/Components/_HumanWritten/ConvertGuest/confirmAndConvertGuest';
import { StaffBookingsListOuter } from 'Views/Components/_HumanWritten/StaffTables/StaffBookings/StaffBookingsListOuter';
import { StaffParkingReservationsListOuter } from 'Views/Components/_HumanWritten/StaffTables/StaffParkingReservations/StaffParkingReservationsListOuter';
import { StaffTransactionsListOuter } from 'Views/Components/_HumanWritten/StaffTables/StaffTransactions/StaffTransactionsListOuter';
import { StaffChartersListOuter } from 'Views/Components/_HumanWritten/StaffTables/StaffCharters/StaffChartersListOuter';
import { Tag } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/CheckInList/Tag';
import { UserDisabledTag, UserDisplayNameTag } from 'Views/Components/_HumanWritten/UserDisplayNameTag';
import GuestTag from 'Views/Components/_HumanWritten/Tag/GuestTag';
import { userTypeRequestKeys } from 'Modules/UserTypeRequests/Hooks/userTypeRequestKeys';
import { FormErrors } from 'Util/_HumanWritten/Errors/FormErrors';
import { useCanEditUser } from '../Hooks/useCanEditUser';
import { useAccountEditSession } from '../Hooks/useAccountEditSession';
import { useConsumeEditModeParam } from '../Hooks/useConsumeEditModeParam';
import { useTabState } from '../Hooks/useTabState';
import AccountPageShell from './AccountPageShell';
import NewEmailUnverifiedBanner from './NewEmailUnverifiedBanner';
import SectionTabs from './SectionTabs';
import UserTypeSection from './UserTypeSection';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';
import { anytimeTicketsLabel } from 'Util/_HumanWritten/anytimeTicketTerm';
import { showUpdateInvoicedBookingsPrompt } from 'Modules/InvoicedBookingReprice/Components/UpdateInvoicedBookingsPrompt';

export interface UserDetailInnerProps {
	user: UserEntity;
	refetchUser: () => void;
	initialTab: string;
	defaultMode?: EntityFormMode;
}

// Tabs whose bodies participate in the shared edit session — switching between them while
// editing is allowed; every other tab is locked until the edit is saved or cancelled.
const EDIT_SESSION_TABS = ['account', 'pricing'];

/**
 * The merged staff view of a user — one page replacing the old /search-results (activity tabs)
 * and /staff-account-details (account tabs) pair, on the account-page shell.
 *
 * The edit session (shared with CustomerAccountInner via useAccountEditSession) lives at page
 * level because one edit and its single Save span the Account AND Pricing tabs: both feed the
 * same form/pricing entity and share `mode`. Refetching swaps this component out via the
 * wrapper's key, so every save/cancel lands back on fresh server data.
 */
export default function UserDetailInner({
	user,
	refetchUser,
	initialTab,
	defaultMode = EntityFormMode.VIEW,
}: UserDetailInnerProps) {
	const intl = useIntl();
	const { isMobile, isIpad } = useDeviceDetect();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { canEdit, isTargetStaff } = useCanEditUser(user);

	const [mode, setMode] = useState<EntityFormMode>(defaultMode);

	// Bumped when something writes a note on this customer, so the notes panel pulls the new one
	// without the staff member having to reload the page to see what they just recorded.
	const [notesRefreshToken, setNotesRefreshToken] = useState(0);
	const [sendingVerification, setSendingVerification] = useState(false);

	// A ?mode=edit deep link only opens the session; it must not survive into the address bar,
	// or the remount that Cancel/Save triggers would put the page straight back into edit mode.
	useConsumeEditModeParam(defaultMode === EntityFormMode.EDIT);

	// Anytime ticketing can be enabled while only staff-facing POS products exist (no customer
	// tickets). In that case the customer Anytime Tickets tab has nothing to show, so gate it on a
	// non-disabled TICKET product existing (mirrors the homepage AnytimeTicketButton). Reuses the
	// already-cached product list query; only fetched when the feature is on.
	const { data: anytimeProducts } = useAnytimeProductList();
	const hasCustomerAnytimeProducts = React.useMemo(
		() => anytimeProducts?.some(p => !p.isDisabled && p.type === 'TICKET') ?? false,
		[anytimeProducts],
	);

	// The page is two stacked cards: a form-width account card and a wider activity card
	// (bookings/transactions want the room; account fields don't). Each has its own tab group,
	// both visible at once so the activity tables are one glance away from the account details.
	const accountTabs = React.useMemo(() => {
		const items = [{ id: 'account', label: 'Account' }];
		// Staff accounts only ever have account details — none of the customer-facing sections
		// (credits, anytime tickets, vehicles, invoices, pricing) apply — so show just the one tab.
		if (isTargetStaff) return items;
		if (appConfigStore.showCreditOnCustomerAccount) items.push({ id: 'credits', label: 'Credits' });
		if (!isTargetStaff && appConfigStore.anytimeTicketingEnabled && hasCustomerAnytimeProducts) {
			items.push({ id: 'entitlements', label: anytimeTicketsLabel() });
		}
		if (!isTargetStaff && appConfigStore.vehicleEnabled && !user.isGuest) {
			items.push({
				id: 'vehicles',
				label: `${intl.formatMessage({ id: 'vehicle__name__label', defaultMessage: 'Vehicle' })}s`,
			});
		}
		if (!isTargetStaff) items.push({ id: 'invoices', label: 'Invoices' });
		if (!isTargetStaff && !user.isGuest) items.push({ id: 'pricing', label: 'Pricing' });
		// Guests included, unlike the customer's own page: a guest cannot sign in to set these, so
		// staff acting on a phone call is the only way their preferences can change at all.
		if (!isTargetStaff) items.push({ id: 'communications', label: 'Communications' });
		return items;
	}, [isTargetStaff, user.isGuest, hasCustomerAnytimeProducts, intl]);

	const activityTabs = React.useMemo(() => {
		const items = [{ id: 'bookings', label: 'Bookings' }];
		if (appConfigStore.parkingEnabled) items.push({ id: 'parking', label: 'Parking reservations' });
		// A charter is neither a booking nor a parking reservation — no route, no departure and no
		// passengers — so it gets a tab of its own rather than rows in the bookings table.
		if (appConfigStore.charterEnabled) items.push({ id: 'charters', label: 'Charters' });
		items.push({ id: 'transactions', label: 'Transactions' });
		return items;
	}, []);

	// The URL's :tab segment addresses whichever card owns that tab id; the other card sits on
	// its default. Selecting a tab in either card points the URL at it.
	const urlFor = (tabId: string) => ROUTES.USER_DETAIL.url(user.id, tabId);
	const [activeAccountTab, setAccountTab] = useTabState({
		tabs: accountTabs,
		initialTab,
		fallbackId: 'account',
		mode,
		urlFor,
		editSessionTabs: EDIT_SESSION_TABS,
		unknownInitialTab: 'ignore',
	});
	const [activeActivityTab, setActivityTab] = useTabState({
		tabs: activityTabs,
		initialTab,
		fallbackId: 'bookings',
		mode,
		urlFor,
		unknownInitialTab: 'ignore',
		// The activity card sits at the bottom of the page; syncing its selection into the URL would
		// change the pathname and make CrudPageLayout scroll back to the top on every switch. Keep it
		// local — deep links still pick the initial tab via initialTab.
		syncUrl: false,
	});

	const session = useAccountEditSession({
		user,
		refetchUser,
		// Staff on-behalf save. The save-account endpoint is authoritative for the customer type's
		// identity role swap (FixedPriceUser / InvoicedUser / User), so the client no longer makes a
		// separate role-update call here.
		performSave: async ({ emailHasChanged, submitUser, newEmail, pricesChanged, clearChangesMade }) => {
			await saveUser(submitUser, user, store.isManager);
			clearChangesMade();

			// Saving a type change fulfils any matching pending user-type request server-side
			// (UserEntity.BeforeSave), so drop the cached pending list — otherwise the request
			// pill survives the post-save remount until the 30s staleTime lapses.
			queryClient.invalidateQueries({ queryKey: userTypeRequestKeys.pending(user.id) });

			if (emailHasChanged) {
				await changeEmailRequestByStaff(user.email, newEmail).catch(() => {
					alertToast('Change email request failed', 'error', 'Error');
				});
			}

			refetchUser();
			alertToast("User's details have been updated!", 'success', 'Details saved');

			// New prices only reach bookings made from now on — the customer's existing bookings keep the fare
			// they were booked at. For an invoiced customer those are still unpaid, so offer to bring them up to
			// date. Managers only: the reprice page is manager-only, so offering it to staff would link them to
			// a page they can't open. Read from the SAVED type, so moving a customer onto an invoiced type
			// prompts and moving them off one doesn't.
			if (pricesChanged && store.isManager && submitUser.userDisplayName?.invoicedUser) {
				showUpdateInvoicedBookingsPrompt(navigate, {
					id: user.id,
					name: `${submitUser.firstName ?? ''} ${submitUser.lastName ?? ''}`.trim(),
				});
			}
		},
		onValidationErrors: (newErrors: FormErrors<UserEntity>) => {
			const pricingErrorKeys = ['prices', 'adultPrice', 'addOnsPrices', 'additionalInformation'];
			if (Object.keys(newErrors).some(k => pricingErrorKeys.includes(k))) {
				setAccountTab('pricing');
			} else {
				setAccountTab('account');
			}
		},
	});
	const { form, errors, saving, markChanged, handleSave, cancelEdit } = session;

	// Leave edit mode as soon as the discard lands — the refetch remount confirms with fresh
	// server data moments later, but the page must not sit in edit mode waiting for it.
	const handleCancel = async () => {
		if (await cancelEdit()) {
			setMode(EntityFormMode.VIEW);
		}
	};

	const canConvertGuest = user.isGuest && canEdit;
	const handleConvertGuest = () => confirmAndConvertGuest(user.email, refetchUser);

	const resendEmailButtons = (
		<>
			<Button
				newStyles
				display={Display.Outline}
				colors={Colors.Secondary}
				sizes={Sizes.Small}
				buttonProps={{ 'data-test-id': 'account-cancel-email-update' } as any}
				onClick={async () => {
					try {
						await cancelChangeEmailRequest(user.id);
						refetchUser();
						alertToast('Cancelled email change request', 'success');
					} catch {
						alertToast('Could not cancel email change request', 'error');
					}
				}}
			>
				Cancel update
			</Button>
			<Button
				newStyles
				display={Display.Solid}
				colors={Colors.Secondary}
				sizes={Sizes.Small}
				disabled={sendingVerification}
				buttonProps={{ 'data-test-id': 'account-resend-verification' } as any}
				onClick={async () => {
					setSendingVerification(true);
					try {
						await changeEmailRequestByStaff(user.email, user.newEmailAddress);
						alertToast('Re-sent verification email', 'success');
						refetchUser();
					} catch {
						alertToast('Could not send verification email', 'error');
					} finally {
						setSendingVerification(false);
					}
				}}
			>
				{sendingVerification ? 'Sending...' : 'Resend verification email'}
			</Button>
		</>
	);

	const headerTitle = (
		<span className="flex flex-wrap items-center gap-2" data-test-id="user-detail-header-title">
			{getCustomerDisplayName(user, true)}
			{UserDisplayNameTag(user?.userDisplayName?.displayName)}
			{isTargetStaff && <Tag text="Staff" />}
			{user.disabled && UserDisabledTag()}
			{user.isGuest && <GuestTag />}
		</span>
	);

	// getFullName() is null when the user has neither name recorded, in which case the crumb renders
	// empty — same as before.
	const breadcrumbs = [{ label: 'Users', to: ROUTES.USERS }, { label: user.getFullName() ?? '' }];

	const subtitleParts = [
		user.email,
		appConfigStore.userPhoneEnabled && user.phone ? user.phone : null,
		user.created ? `Customer since ${formatStandardDate(user.created)}` : null,
		// Omitted when empty — last login is only tracked since the feature was introduced,
		// so a missing value doesn't necessarily mean "never logged in".
		user.lastLoginAt ? `Last login ${formatStandardDateTime(user.lastLoginAt)}` : null,
	].filter(Boolean);

	const hasPendingNewEmail = isNotNullOrUndefined(user.newEmailAddress) && user.newEmailAddress !== user.email;

	// Accounts staff created for a customer hold a generated password the customer has never seen,
	// so what they are missing is an activation (set a password), not a verification. The re-send
	// endpoint picks the matching email off the same flag — this only keeps the wording honest about
	// what staff are about to send. A pending email change takes precedence: that one has to go to
	// the new address, which only the change-email re-send does.
	const needsActivation = user.needsPasswordReset && !hasPendingNewEmail;
	const unverifiedEmailLabel = needsActivation ? 'Resend activation email' : 'Resend verification email';

	const banners = (
		<>
			<If condition={!user.emailConfirmed}>
				<div className="pt-4">
					<MessageBox
						title={needsActivation ? 'Account Not Activated' : 'Account Unverified'}
						status="warning"
						actions={
							<Button
								newStyles
								display={Display.Solid}
								colors={Colors.Secondary}
								sizes={Sizes.Small}
								disabled={sendingVerification}
								onClick={async () => {
									setSendingVerification(true);
									try {
										if (hasPendingNewEmail) {
											await changeEmailRequestByStaff(user.email, user.newEmailAddress);
											refetchUser();
										} else {
											await resendVerification(user.id);
										}
										alertToast(
											needsActivation ? 'Activation email sent' : 'Verification email sent',
											'success',
										);
									} catch {
										alertToast(
											needsActivation
												? 'Could not send activation email'
												: 'Could not send verification email',
											'error',
										);
									} finally {
										setSendingVerification(false);
									}
								}}
							>
								{sendingVerification ? 'Sending...' : unverifiedEmailLabel}
							</Button>
						}
						slim
						testId="account-unverified-notice"
					>
						{
							// eslint-disable-next-line no-nested-ternary
							hasPendingNewEmail
								? 'This customer has not verified their account yet and is pending ' +
									`verification of a new email address (${user.newEmailAddress}). ` +
									"Verification ensures they'll receive all email communications."
								: needsActivation
									? 'This customer has not activated their account yet. Activation lets ' +
										"them set a password, and ensures they'll receive all email " +
										'communications.'
									: 'This customer has not verified their account yet. Verification ensures ' +
										"they'll receive all email communications."
						}
					</MessageBox>
				</div>
			</If>
			<NewEmailUnverifiedBanner user={user} actions={resendEmailButtons} />
		</>
	);

	const renderAccountTab = () => {
		switch (activeAccountTab) {
			case 'credits':
				return <AccountDetailsVouchers user={user} />;
			case 'entitlements':
				return <UserEntitlementsWrapper user={user} />;
			case 'vehicles':
				return <AccountDetailsVehicles user={user} staffView />;
			case 'invoices':
				return <UserInvoiceListComponent userId={user.id} />;
			case 'communications':
				return (
					<StaffCommunicationPreferences
						userId={user.id}
						customerName={`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()}
						onSaved={() => setNotesRefreshToken(token => token + 1)}
					/>
				);
			case 'pricing':
				return (
					<UserPricingFields
						form={form}
						mode={mode}
						setMode={setMode}
						setNeedConfirm={markChanged}
						errors={errors}
						canEdit={canEdit}
					/>
				);
			case 'account':
			default:
				return (
					<DetailFields
						user={user}
						form={form}
						mode={mode}
						setMode={setMode}
						staffView
						errors={errors}
						canEdit={canEdit}
						onConvertGuest={canConvertGuest ? handleConvertGuest : undefined}
						userTypeSlot={
							!isTargetStaff && !user.isGuest ? (
								<UserTypeSection
									user={user}
									form={form}
									mode={mode}
									canEdit={canEdit}
									onTypeAssigned={refetchUser}
								/>
							) : undefined
						}
					/>
				);
		}
	};

	const renderActivityTab = () => {
		switch (activeActivityTab) {
			case 'parking':
				return <StaffParkingReservationsListOuter userId={user.id} onResultsPage />;
			case 'charters':
				return <StaffChartersListOuter userId={user.id} onResultsPage />;
			case 'transactions':
				return <StaffTransactionsListOuter userId={user.id} onResultsPage />;
			case 'bookings':
			default:
				return <StaffBookingsListOuter userId={user.id} onResultsPage />;
		}
	};

	/*
		Activity card — the customer's bookings a glance away from the account details.
		Desktop: near full-viewport width with only a small outside margin, so the tables get the room
		they need without matching the account card's form width, and it sits below the account card.
		Small screens: first, above the account card. Staff open a customer to look at their bookings
		far more often than to edit them, and below a phone-length stack of account fields the tables
		were a long scroll away — staff reported not realising they were there at all.
		Mobile: a full-bleed band instead (no margin, no card chrome) — every pixel of width goes to
		the tables. Only two/three short tabs, so the desktop tab row works on mobile too.
		Staff accounts never have bookings/transactions/parking reservations, so the whole activity
		card is omitted for them.
	*/
	const activityFirst = isIpad;
	const activityCard = isTargetStaff ? null : (
		<div
			className={classNames(activityFirst ? 'mb-4 mt-4' : 'mt-8', !isMobile && 'px-4 md:px-6')}
			data-test-id="user-detail-activity"
		>
			<SectionTabs
				tabs={activityTabs}
				activeTab={activeActivityTab}
				onChange={setActivityTab}
				isMobile={false}
				tabsClassName={isMobile ? 'border-y border-gray-200' : 'rounded-t-xl border border-gray-200'}
			/>
			<div
				className={classNames(
					'account-page bg-white',
					isMobile
						? 'border-b border-gray-200 px-2 py-4'
						: 'rounded-b-xl border border-t-0 border-gray-200 p-4 md:p-6',
				)}
			>
				{renderActivityTab()}
			</div>
		</div>
	);

	return (
		<AccountPageShell
			className="user-detail-page"
			title={headerTitle}
			subtitle={subtitleParts.join(' · ')}
			breadcrumbs={breadcrumbs}
			mode={mode}
			saving={saving}
			onSave={handleSave}
			onCancel={handleCancel}
			banners={banners}
			tabs={accountTabs}
			activeTab={activeAccountTab}
			onTabChange={setAccountTab}
			editSessionTabs={EDIT_SESSION_TABS}
			pagePadding="outside"
			leadingContent={activityFirst ? activityCard : undefined}
			extraContent={
				<>
					{!activityFirst && activityCard}

					<If condition={mode !== EntityFormMode.EDIT}>
						<UserNotesModal userId={user.id} refreshToken={notesRefreshToken} />
					</If>
				</>
			}
		>
			{renderAccountTab()}
		</AccountPageShell>
	);
}
